// EmployeeDashboard.js - Updated with Team Calendar Links
import React, { useState, useEffect, useRef, useCallback } from "react";
import { CalendarToday as CalendarTodayIcon, Assessment as AssessmentIcon } from '@mui/icons-material';
import EmployeeTimeOffSummary from "../components/EmployeeTimeOffSummary";
import TimeOffNotificationsComponent from "../components/TimeOffNotificationsComponent"; 
import { checkIPLocation } from "../services/IPVerification";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import {
    AppBar,
    Toolbar,
    Typography,
    Button,
    Container,
    Paper,
    Box,
    CircularProgress,
    Alert,
    Card,
    CardContent,
    Grid,
    Chip,
    ListItem,
    ListItemText,
    List,
    Divider,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Snackbar,
    IconButton,
    Tabs,
    Tab
} from "@mui/material";
import {
    collection,
    addDoc,
    serverTimestamp,
    query,
    where,
    orderBy,
    limit,
    getDocs,
    Timestamp
} from "firebase/firestore";
import {
    AccessTime as AccessTimeIcon,
    LocationOn as LocationOnIcon,
    Close as CloseIcon,
    ArrowUpward as ArrowUpwardIcon,
    ArrowDownward as ArrowDownwardIcon,
    Wifi as WifiIcon,
    QrCode as QrCodeIcon,
    Settings as SettingsIcon,
    People as PeopleIcon,
    Dashboard as DashboardIcon,
} from '@mui/icons-material';
import QRScanner from "../components/QRScanner";
import { checkWiFiNetwork } from "../components/WiFiVerification";
import { sendNotificationToAdmins } from "../services/NotificationService";
import { checkLocationInGeofence } from "../services/GeofenceService";

function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`verification-tabpanel-${index}`}
            aria-labelledby={`verification-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ py: 2 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function EmployeeDashboard({ user, orgId }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState("");
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [recentLogs, setRecentLogs] = useState([]);
    const [lastClockIn, setLastClockIn] = useState(null);
    const [isPerformingClockAction, setIsPerformingClockAction] = useState(false);
    const [orgName, setOrgName] = useState("");
    const [locationError, setLocationError] = useState(false);
    const [confirmationDialog, setConfirmationDialog] = useState({
        open: false,
        action: ""
    });
    const [elapsedTime, setElapsedTime] = useState(null);

    // Verification related states
    const [verificationTabValue, setVerificationTabValue] = useState(0);
    const [verificationStatus, setVerificationStatus] = useState({
        verified: false,
        method: null,
        locationName: null,
        message: null
    });
    const [verificationDialog, setVerificationDialog] = useState({
        open: false,
        action: ""
    });
    const [wifiVerificationLoading, setWifiVerificationLoading] = useState(false);
    const [wifiVerificationResult, setWifiVerificationResult] = useState(null);

    const currentStatusRef = useRef(null);
    const lastClockInRef = useRef(null);
    const fetchTimeoutRef = useRef(null);
    const handleLogout = async () => {
        await signOut(auth);
        window.location.href = "/login";
    };

    const handleAction = async (type) => {
        // 🔔 DEBUG LOGGING - ACTION START
        console.log("=".repeat(80));
        console.log("🔔 HANDLE ACTION - Type:", type);
        console.log("🔔 Current status before action:", status);
        console.log("🔔 Last clock in:", lastClockIn);
        console.log("🔔 Is performing clock action?:", isPerformingClockAction);
        console.log("=".repeat(80));
        // Prevent concurrent clock actions
        if (isPerformingClockAction) {
            console.log("Clock action already in progress, ignoring...");
            return;
        }

        // Close dialogs first
        setConfirmationDialog({ open: false, action: "" });

        // Check verification ONLY for clock in
        if (type === "clockIn" && !verificationStatus.verified) {
            setVerificationDialog({
                open: true,
                action: type
            });
            return;
        }

        setIsPerformingClockAction(true); // 🔧 Lock the action
        setLoading(true);
        setMessage("");
        setLocationError(false);

        try {
            // Get current location
            const location = await new Promise((resolve, reject) => {
                navigator.geolocation.getCurrentPosition(
                    pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
                    error => {
                        console.error("Geolocation error:", error);
                        setLocationError(true);
                        resolve(null); // Continue without location
                    },
                    { timeout: 10000, enableHighAccuracy: true }
                );
            });

            const logData = {
                userId: user.uid,
                email: user.email,
                type,
                timestamp: serverTimestamp(), // Always use server timestamp
                location
            };

            if (type === "clockIn" && verificationStatus.verified) {
                logData.verification = {
                    method: verificationStatus.method,
                    timestamp: serverTimestamp(), // Use server timestamp here too
                    locationName: verificationStatus.locationName
                };
            }

            await addDoc(collection(db, "organizations", orgId, "timeLogs"), logData);
            // 🔔 DEBUG LOGGING - FIRESTORE WRITE
            console.log("=".repeat(80));
            console.log("🔔 FIRESTORE - Successfully wrote log entry");
            console.log("🔔 Log data saved:", logData);
            console.log("=".repeat(80));

            if (type === "clockIn") {
                const now = new Date();

                // ✅ DEBUG LOGGING - CLOCK IN
                console.log("✅ CLOCK IN SUCCESSFUL - Setting status to clockIn");
                console.log("✅ Time:", now.toLocaleTimeString());
                console.log("=".repeat(80));

                setStatus("clockIn");
                setLastClockIn(now);
                currentStatusRef.current = "clockIn";
                lastClockInRef.current = now;
                setMessage(`✅ You have successfully clocked in at ${now.toLocaleTimeString()}.`);
            } else {

                // ❌ DEBUG LOGGING - CLOCK OUT
                console.log("❌ CLOCK OUT SUCCESSFUL - Setting status to clockOut");
                console.log("❌ Time:", new Date().toLocaleTimeString());
                console.log("=".repeat(80));

                setStatus("clockOut");
                setLastClockIn(null);
                currentStatusRef.current = "clockOut";
                lastClockInRef.current = null;
                setMessage(`✅ You have successfully clocked out at ${new Date().toLocaleTimeString()}.`);

                // Reset verification status on clock out
                setVerificationStatus({
                    verified: false,
                    method: null,
                    locationName: null,
                    message: null
                });
            }

            setShowSnackbar(true);

            setTimeout(() => {
                fetchRecentLogs();
            }, 1500); // Wait 1.5 seconds before refreshing

        } catch (err) {
            setMessage(`❌ Error: ${err.message}`);
        } finally {
            setLoading(false);
            setIsPerformingClockAction(false); // 🔧 Unlock the action
        }
    };

    const confirmAction = (action) => {
        if (isPerformingClockAction) {
            return; // Prevent multiple confirmations
        }

        // For clock in, check if verification is needed
        if (action === "clockIn" && !verificationStatus.verified) {
            setVerificationDialog({
                open: true,
                action
            });
            return;
        }

        setConfirmationDialog({
            open: true,
            action
        });
    };

    const fetchRecentLogs = useCallback(async () => {
        // Prevent concurrent fetches
        if (fetchTimeoutRef.current) {
            clearTimeout(fetchTimeoutRef.current);
        }

        fetchTimeoutRef.current = setTimeout(async () => {
            try {
                const logsRef = collection(db, "organizations", orgId, "timeLogs");
                const q = query(
                    logsRef,
                    where("userId", "==", user.uid),
                    orderBy("timestamp", "desc"),
                    limit(10)
                );

                const snapshot = await getDocs(q);
                const logs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
                
                // 📊 DEBUG LOGGING - START
                console.log("=".repeat(80));
                console.log("📊 FETCH LOGS - Total logs retrieved:", logs.length);
                console.log("📊 FETCH LOGS - All logs:", logs);
                console.log("=".repeat(80));
                // 📊 DEBUG LOGGING - END

                setRecentLogs(logs);

                // 🔧 FIX: Filter out synthetic entries to find the last REAL clock action
                const realLogs = logs.filter(log => !log.syntheticEntry && (log.type === "clockIn" || log.type === "clockOut"));
                const lastRealLog = realLogs.length > 0 ? realLogs[0] : null;

                // 📊 DEBUG LOGGING - LAST LOG ANALYSIS
                console.log("=".repeat(80));
                console.log("📊 LAST LOG ANALYSIS");
                console.log("📊 All logs count:", logs.length);
                console.log("📊 Real logs count:", realLogs.length);
                console.log("📊 Last REAL log:", lastRealLog);
                console.log("📊 Last REAL log type:", lastRealLog?.type);
                console.log("📊 Is synthetic?:", lastRealLog?.syntheticEntry);
                console.log("📊 Has timestamp?:", !!lastRealLog?.timestamp);
                console.log("=".repeat(80));

                // Use lastRealLog instead of lastLog for status determination
                if (lastRealLog && lastRealLog.type === "clockIn" && lastRealLog.timestamp) {
                    const clockInTime = lastRealLog.timestamp.toDate();  // ← Changed from lastLog

                    // ✅ DEBUG LOGGING
                    console.log("✅ STATUS UPDATE - Setting to CLOCKED IN at:", clockInTime);
                    console.log("=".repeat(80));

                    setLastClockIn(clockInTime);
                    setStatus("clockIn");
                    currentStatusRef.current = "clockIn";
                    lastClockInRef.current = clockInTime;
                } else {
                    // ❌ DEBUG LOGGING
                    console.log("❌ STATUS UPDATE - Setting to CLOCKED OUT");
                    console.log("❌ Reason: Last log type is NOT 'clockIn' OR no timestamp");
                    console.log("=".repeat(80));

                    setLastClockIn(null);
                    setStatus("clockOut");
                    currentStatusRef.current = "clockOut";
                    lastClockInRef.current = null;

                    // 🔧 FIX 6: Only reset verification if we're actually clocked out
                    setVerificationStatus({
                        verified: false,
                        method: null,
                        locationName: null,
                        message: null
                    });
                }
            } catch (err) {
                console.error("Error fetching logs:", err);
            }
        }, 500); // 500ms debounce
    }, [orgId, user.uid]);

    const fetchOrgName = async () => {
        try {
            const orgDoc = await getDocs(collection(db, "organizations"));
            orgDoc.forEach((doc) => {
                if (doc.id === orgId) {
                    setOrgName(doc.data().name);
                }
            });
        } catch (err) {
            console.error("Error fetching organization details:", err);
        }
    };

    useEffect(() => {
        return () => {
            if (fetchTimeoutRef.current) {
                clearTimeout(fetchTimeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (user?.uid && orgId) {
            fetchRecentLogs();
            fetchOrgName();
        }
    }, [user?.uid, orgId, fetchRecentLogs]);

    // Timer effect for calculating elapsed time since clock in
    useEffect(() => {
        let timer;
        if (lastClockIn) {
            timer = setInterval(() => {
                const now = new Date();
                const elapsed = now - lastClockIn;

                // Format elapsed time
                const hours = Math.floor(elapsed / (1000 * 60 * 60));
                const minutes = Math.floor((elapsed % (1000 * 60 * 60)) / (1000 * 60));
                const seconds = Math.floor((elapsed % (1000 * 60)) / 1000);

                setElapsedTime({
                    hours,
                    minutes,
                    seconds,
                    total: elapsed
                });
            }, 1000);
        } else {
            setElapsedTime(null);
        }

        return () => {
            if (timer) clearInterval(timer);
        };
    }, [lastClockIn]);

    // Format elapsed time for display
    const formatElapsedTime = () => {
        if (!elapsedTime) return "0h 0m 0s";

        return `${elapsedTime.hours}h ${elapsedTime.minutes}m ${elapsedTime.seconds}s`;
    };

    // Handle verification tab change
    const handleVerificationTabChange = (event, newValue) => {
        setVerificationTabValue(newValue);
    };

    const verifyIPLocation = async () => {
        setWifiVerificationLoading(true);
        setWifiVerificationResult(null);

        try {
            const result = await checkIPLocation(orgId);

            setWifiVerificationResult({
                isApproved: result.isApproved,
                message: result.message,
                currentIP: result.currentIP
            });

            if (result.isApproved) {
                setVerificationStatus({
                    verified: true,
                    method: "ip",
                    locationName: result.ipName || "Office Network",
                    message: result.message
                });

                setVerificationDialog({ open: false, action: "" });
                setConfirmationDialog({
                    open: true,
                    action: "clockIn"
                });
            }
        } catch (error) {
            console.error("IP verification error:", error);
            setWifiVerificationResult({
                isApproved: false,
                message: `Error: ${error.message}. Please try again or contact your administrator.`
            });
        } finally {
            setWifiVerificationLoading(false);
        }
    };

    // Handle QR code verification result
    const handleQRVerificationComplete = (result) => {
        if (result.success) {
            setVerificationStatus({
                verified: true,
                method: "qrCode",
                locationName: result.locationName,
                message: "Location verified via QR code"
            });

            // Close verification dialog and open confirmation dialog
            setVerificationDialog({ open: false, action: "" });

            // Automatically open confirmation dialog
            setConfirmationDialog({
                open: true,
                action: "clockIn"
            });
        } else {
            // QR verification failed
            setVerificationStatus({
                verified: false,
                method: null,
                locationName: null,
                message: result.error || "QR code verification failed"
            });
        }
    };

    // Close verification dialog
    const closeVerificationDialog = () => {
        setVerificationDialog({
            ...verificationDialog,
            open: false
        });
    };

    return (
        <>
            <AppBar position="static">
                <Toolbar>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                        TimeWarden
                    </Typography>
                    <IconButton
                        color="inherit"
                        onClick={() => window.location.href = "/employee/settings"}
                        sx={{ mr: 1 }}
                    >
                        <SettingsIcon />
                    </IconButton>
                    <Button color="inherit" onClick={handleLogout}>Logout</Button>
                </Toolbar>
            </AppBar>

            <Container maxWidth="md">
                <Paper sx={{ mt: 5, p: 4 }}>
                    <Typography variant="h5" gutterBottom>Employee Dashboard</Typography>
                    <Typography variant="body1" gutterBottom>
                        Welcome, {user.email}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                        Organization: {orgName || orgId}
                    </Typography>

                    {/* Verification Status */}
                    {verificationStatus.verified && (
                        <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
                            <Typography variant="body1">
                                ✓ Location verified via {verificationStatus.method === "wifi" ? "Wi-Fi network" : "QR code"}
                            </Typography>
                            {verificationStatus.locationName && (
                                <Typography variant="body2">
                                    Location: {verificationStatus.locationName}
                                </Typography>
                            )}
                        </Alert>
                    )}

                    {/* Status Card */}
                    <Card sx={{ mt: 3, mb: 4, borderRadius: 2, boxShadow: 3 }}>
                        <CardContent>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={6}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Typography variant="h6" gutterBottom>
                                            Current Status
                                        </Typography>
                                        <Chip
                                            label={status === "clockIn" ? "Clocked In" : "Clocked Out"}
                                            color={status === "clockIn" ? "success" : "error"}
                                            icon={status === "clockIn" ? <ArrowUpwardIcon /> : <ArrowDownwardIcon />}
                                            sx={{ fontSize: '1rem', py: 2, px: 1 }}
                                        />

                                        {status === "clockIn" && lastClockIn && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="body2" color="text.secondary">
                                                    <AccessTimeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                                    Clocked in at {lastClockIn.toLocaleTimeString()}
                                                </Typography>
                                                <Typography variant="body1" sx={{ fontWeight: 'bold', mt: 1 }}>
                                                    Time elapsed: {formatElapsedTime()}
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>
                                </Grid>

                                <Grid item xs={12} md={6}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Button
                                            variant="contained"
                                            color="success"
                                            size="large"
                                            disabled={loading || status === "clockIn" || isPerformingClockAction} // 🔧 Add isPerformingClockAction
                                            onClick={() => confirmAction("clockIn")}
                                            startIcon={<ArrowUpwardIcon />}
                                            fullWidth
                                            sx={{ py: 1.5 }}
                                        >
                                            {(loading || isPerformingClockAction) ? <CircularProgress size={24} /> : "Clock In"}
                                        </Button>

                                        <Button
                                            variant="contained"
                                            color="error"
                                            size="large"
                                            disabled={loading || status !== "clockIn" || isPerformingClockAction} // 🔧 Add isPerformingClockAction
                                            onClick={() => confirmAction("clockOut")}
                                            startIcon={<ArrowDownwardIcon />}
                                            fullWidth
                                            sx={{ py: 1.5 }}
                                        >
                                            {(loading || isPerformingClockAction) ? <CircularProgress size={24} /> : "Clock Out"}
                                        </Button>
                                    </Box>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {/* Feature Buttons Section - NEW */}
                    <Grid container spacing={2} sx={{ mb: 4 }}>
                        <Grid item xs={12} sm={4}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => window.location.href = "/timeoff"}
                                startIcon={<CalendarTodayIcon />}
                                fullWidth
                                sx={{ py: 1.5 }}
                            >
                                Request Time Off
                            </Button>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => window.location.href = "/employee/calendar"}
                                startIcon={<PeopleIcon />}
                                fullWidth
                                sx={{ py: 1.5 }}
                            >
                                Team Calendar
                            </Button>
                        </Grid>

                        <Grid item xs={12} sm={4}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => window.location.href = "/employee/calendar?tab=2"}
                                startIcon={<DashboardIcon />}
                                fullWidth
                                sx={{ py: 1.5 }}
                            >
                                Time Dashboard
                            </Button>
                        </Grid>
                    </Grid>

                    {locationError && (
                        <Alert severity="warning" sx={{ mb: 3 }}>
                            Location services are disabled. Your clock in/out will be recorded without location information.
                        </Alert>
                    )}

                    {/* Team Time Off Notifications - new section */}
                    <TimeOffNotificationsComponent userId={user.uid} orgId={orgId} />

                    {/* Time Off Summary Section */}
                    <EmployeeTimeOffSummary userId={user.uid} orgId={orgId} />

                    {/* Recent Activity */}
                    <Box sx={{ mt: 4 }}>
                        <Typography variant="h6" gutterBottom>Recent Activity</Typography>

                        {recentLogs.length === 0 ? (
                            <Alert severity="info">
                                No recent activity. Your clock in/out history will appear here.
                            </Alert>
                        ) : (
                            <List sx={{ bgcolor: 'background.paper', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                {recentLogs.map((log, index) => (
                                    <React.Fragment key={log.id}>
                                        <ListItem alignItems="flex-start">
                                            <ListItemText
                                                primary={
                                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                                        {log.type === "timeOff" ? (
                                                            <Chip
                                                                label={`${log.timeOffLabel || log.timeOffType || "Time Off"} - ${log.subType === "clockIn" ? "Start" : "End"}`}
                                                                size="small"
                                                                color="warning"
                                                                sx={{ mr: 1 }}
                                                                icon={<CalendarTodayIcon />}
                                                            />
                                                        ) : (
                                                            <Chip
                                                                label={log.type === "clockIn" ? "Clocked In" : "Clocked Out"}
                                                                size="small"
                                                                color={log.type === "clockIn" ? "success" : "error"}
                                                                sx={{ mr: 1 }}
                                                            />
                                                        )}
                                                        <Typography>
                                                            {log.timestamp ? log.timestamp.toDate().toLocaleString() : "Pending..."}
                                                        </Typography>
                                                        {log.syntheticEntry && (
                                                            <Chip
                                                                label="Auto"
                                                                size="small"
                                                                variant="outlined"
                                                                sx={{ ml: 1, height: 20 }}
                                                            />
                                                        )}
                                                    </Box>
                                                }
                                                secondary={
                                                    <>
                                                        {log.location && !log.syntheticEntry ? (
                                                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                                                                <LocationOnIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                                                {log.location.lat.toFixed(6)}, {log.location.lng.toFixed(6)}
                                                            </Typography>
                                                        ) : log.syntheticEntry ? (
                                                            <Typography variant="body2" sx={{ mt: 0.5, color: 'warning.main' }}>
                                                                Automatic entry from approved time off request
                                                            </Typography>
                                                        ) : (
                                                            "No location data"
                                                        )}

                                                        {log.verification && !log.syntheticEntry && (
                                                            <Typography variant="body2" sx={{ mt: 0.5, color: 'success.main' }}>
                                                                {log.verification.method === "wifi" ? (
                                                                    <WifiIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                                                ) : (
                                                                    <QrCodeIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                                                                )}
                                                                Verified via {log.verification.method === "wifi" ? "Wi-Fi" : "QR Code"}
                                                                {log.verification.locationName && ` at ${log.verification.locationName}`}
                                                            </Typography>
                                                        )}
                                                    </>
                                                }
                                            />
                                        </ListItem>
                                        {index < recentLogs.length - 1 && <Divider />}
                                    </React.Fragment>
                                ))}
                            </List>
                        )}
                    </Box>
                </Paper>
            </Container>

            {/* Confirmation Dialog */}
            <Dialog open={confirmationDialog.open} onClose={() => setConfirmationDialog({ open: false, action: "" })}>
                <DialogTitle>
                    {confirmationDialog.action === "clockIn" ? "Clock In Confirmation" : "Clock Out Confirmation"}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {confirmationDialog.action === "clockIn"
                            ? `Are you sure you want to clock in now? Your current location${verificationStatus.locationName ? ` (${verificationStatus.locationName})` : ''} will be recorded.`
                            : "Are you sure you want to clock out now? This will end your current shift."}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmationDialog({ open: false, action: "" })}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => handleAction(confirmationDialog.action)}
                        color={confirmationDialog.action === "clockIn" ? "success" : "error"}
                        variant="contained"
                    >
                        {confirmationDialog.action === "clockIn" ? "Clock In" : "Clock Out"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Verification Dialog */}
            <Dialog
                open={verificationDialog.open}
                onClose={closeVerificationDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Verify Your Location
                </DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Please verify your location using one of the following methods:
                    </DialogContentText>

                    <Tabs
                        value={verificationTabValue}
                        onChange={handleVerificationTabChange}
                        variant="fullWidth"
                        sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
                    >
                        <Tab
                            icon={<LocationOnIcon />}
                            label="IP Location"
                            id="verification-tab-0"
                            aria-controls="verification-tabpanel-0"
                        />
                        <Tab
                            icon={<QrCodeIcon />}
                            label="QR Code"
                            id="verification-tab-1"
                            aria-controls="verification-tabpanel-1"
                        />
                    </Tabs>

                    <TabPanel value={verificationTabValue} index={0}>
                        <Typography variant="body1" gutterBottom>
                            Verify that you are connected to your approved office network.
                        </Typography>

                        {wifiVerificationResult && (
                            <Alert
                                severity={wifiVerificationResult.isApproved ? "success" : "error"}
                                sx={{ my: 2 }}
                            >
                                {wifiVerificationResult.message}
                                {wifiVerificationResult.currentIP && (
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Your current IP: {wifiVerificationResult.currentIP}
                                    </Typography>
                                )}
                            </Alert>
                        )}

                        <Button
                            variant="contained"
                            color="primary"
                            onClick={verifyIPLocation}
                            disabled={wifiVerificationLoading}
                            startIcon={<LocationOnIcon />}
                            sx={{ mt: 2 }}
                        >
                            {wifiVerificationLoading ? (
                                <CircularProgress size={24} />
                            ) : "Verify Office Location"}
                        </Button>
                    </TabPanel>

                    <TabPanel value={verificationTabValue} index={1}>
                        <Typography variant="body1" gutterBottom>
                            Scan the QR code displayed at your workplace to verify your location.
                        </Typography>

                        <QRScanner
                            orgId={orgId}
                            onVerificationComplete={handleQRVerificationComplete}
                        />
                    </TabPanel>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeVerificationDialog}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Snackbar */}
            <Snackbar
                open={showSnackbar}
                autoHideDuration={6000}
                onClose={() => setShowSnackbar(false)}
                message={message}
                action={
                    <IconButton
                        size="small"
                        color="inherit"
                        onClick={() => setShowSnackbar(false)}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                }
            />
        </>
    );
}

export default EmployeeDashboard;